// KÉP-NAGYÍTÓ (lightbox) — a tárolt tartalomba beágyazott képek teljes méretű megtekintése, léptetéssel.
//
// MIÉRT KELL: a jegy-leírásba és a kommentekbe ragasztott képek jellemzően KÉPERNYŐFELVÉTELEK. A szál
// szövegszélességére zsugorítva olvashatatlanok, márpedig épp azért kerültek oda, hogy a részletet mutassák.
// A kattintásra nagyító + a képek közti léptetés az iparági alap (GitHub, Linear, Slack, Jira).
//
// MIÉRT SAJÁT KOMPONENS, ÉS NEM CSOMAG: a feladat szűk (egy overlay, két nyíl, három billentyű), a
// képeink viszont NEM egyszerű URL-ek — a `src` egy rövid életű presigned URL, amit a `RichTextContent`
// hidratálása tesz a DOM-ba. Egy általános lightbox-csomagnak a kép-LISTÁT kellene átadni, azt pedig a
// tartalomból kell összegyűjteni — vagyis a nehéz rész (a DOM-ból gyűjtés) így is a miénk lenne, plusz egy
// dependency és a saját stílus-világa. A `Dialog` primitívünk (Radix) adja a fókusz-csapdát és az ESC-et.
//
// A KOMPONENS SEMMIT nem tud a doménről: kap egy `src`-listát és egy kezdő indexet.
import {
  ArrowTopRightOnSquareIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

export interface MediaLightboxItem {
  /** A MEGJELENÍTHETŐ forrás (nálunk friss presigned URL — a `RichTextContent` hidratálásából). */
  src: string;
  /** Alternatív szöveg / fájlnév — a fejlécben és a `alt`-ban. */
  alt: string;
  /** `video` → `<video controls>`; alap: `image`. */
  kind?: 'image' | 'video';
}

export interface MediaLightboxProps {
  items: readonly MediaLightboxItem[];
  /** A megnyitáskor mutatott elem indexe; `null` → zárva. */
  index: number | null;
  onClose: () => void;
  onIndexChange: (index: number) => void;
}

export function MediaLightbox({
  items,
  index,
  onClose,
  onIndexChange,
}: MediaLightboxProps): JSX.Element | null {
  const { t } = useTranslation();
  const open = index !== null && index >= 0 && index < items.length;

  const step = useCallback(
    (delta: number) => {
      if (index === null || items.length === 0) return;
      // KÖRBEFORDUL: az utolsóról a jobb nyíl az elsőre visz. Egy tiltott nyíl itt csak zsákutca lenne —
      // a felhasználó nem tudja, hogy a sor végén van, amíg meg nem próbálja.
      onIndexChange((index + delta + items.length) % items.length);
    },
    [index, items.length, onIndexChange],
  );

  // BILLENTYŰZET: ESC zár, a nyilak léptetnek. A `Dialog`-ot SZÁNDÉKOSAN nem használjuk (lásd a fájl
  // fejlécét: a tartalom egy teljes-viewport kép, nem panel), ezért a kezelés itt van — de a viselkedés
  // ugyanaz, amit a rendszer minden modálján megszokott a felhasználó.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'ArrowLeft') step(-1);
    };
    document.addEventListener('keydown', onKey);
    // A HÁTTÉR-GÖRGETÉS ZÁROLVA: nyitott nagyítón a görgő különben a lapot mozgatta a kép alatt.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, step]);

  if (!open) return null;
  const current = items[index];
  if (current === undefined) return null;
  const many = items.length > 1;

  /**
   * A NAGYÍTÓ A `document.body`-BA PORTÁLOZVA renderel — NEM a hívó helyén.
   *
   * MIÉRT KÖTELEZŐ (user-lelet: „világos nézetben mind a 4 sarokban egy kis fehér pötty", és
   * „a leírásban lévő képre kattintva felül végig látszik egy fehér sáv"): a `position: fixed` NEM a
   * viewporthoz igazodik, ha egy ŐSE containing blockot csinál — és a `backdrop-filter` (a `card-glass` /
   * `sidebar-glass` osztályaink) pontosan ezt teszi, a `transform`/`filter` mellett. A nagyító így a
   * KÁRTYA téglalapjára szorult: a kártya `rounded-*` + `overflow-hidden`-je levágta a sarkait (onnan a
   * négy fehér pötty), a kártya felső széle alatt pedig kilátszott a lap háttere (a fehér sáv).
   *
   * A portál a DOM-fából kiviszi az overlayt, tehát semmilyen ős nem tudja se elvágni, se elmozdítani.
   */
  return createPortal(
    <div
      // `role="dialog"` + `aria-modal`: a képernyőolvasó modálisként jelenti be. A NÉV a fájlnév/alt.
      role="dialog"
      aria-modal="true"
      aria-label={current.alt || t('lightbox.title')}
      className="app-fixed-fill fixed inset-0 z-[60] flex flex-col bg-black/85 backdrop-blur-sm"
      // A HÁTTÉRRE kattintás zár — de CSAK a háttérre: a képre kattintás nem (különben a léptetés közben
      // egy pontatlan kattintás bezárná a nagyítót).
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* FEJLÉC: fájlnév · sorszám · új lapon megnyitás · bezárás. A sorszám csak több elemnél. */}
      <div className="flex shrink-0 items-center gap-3 px-4 py-3 text-white">
        <span className="min-w-0 flex-1 truncate text-sm">{current.alt}</span>
        {many && (
          <span className="shrink-0 text-xs tabular-nums text-white/70">
            {index + 1} / {items.length}
          </span>
        )}
        {/* ÚJ LAPON: a presigned URL rövid életű, de a MEGNYITÁS pillanatában érvényes — így a felhasználó
            a böngésző saját nagyítójával/mentésével is dolgozhat (a letöltés a tárból megy, nem rajtunk át). */}
        <a
          href={current.src}
          target="_blank"
          rel="noreferrer"
          aria-label={t('lightbox.openInNewTab')}
          title={t('lightbox.openInNewTab')}
          className="ease-atlas shrink-0 rounded-lg p-1.5 text-white/80 transition-colors duration-150 hover:bg-white/10 hover:text-white"
        >
          <ArrowTopRightOnSquareIcon className="size-5" />
        </a>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('lightbox.close')}
          title={t('lightbox.close')}
          className="ease-atlas shrink-0 rounded-lg p-1.5 text-white/80 transition-colors duration-150 hover:bg-white/10 hover:text-white"
        >
          <XMarkIcon className="size-5" />
        </button>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 pb-4">
        {many && (
          <button
            type="button"
            onClick={() => step(-1)}
            aria-label={t('lightbox.prev')}
            className="ease-atlas absolute left-2 z-10 rounded-full bg-black/40 p-2 text-white/85 transition-colors duration-150 hover:bg-black/70 hover:text-white"
          >
            <ChevronLeftIcon className="size-6" />
          </button>
        )}

        {current.kind === 'video' ? (
          // A VIDEÓ a nagyítóban is a böngésző saját lejátszójával megy (`controls`) — nincs saját
          // lejátszó-felület. Az `autoPlay` SZÁNDÉKOSAN nincs: a hang váratlan megszólalása zavaró.
          // FELIRAT (`<track>`) SZÁNDÉKOSAN NINCS: felhasználó által feltöltött felvételről van szó, amihez
          // nem létezik feliratfájl — egy üres `<track>` csak hamis akadálymentesítés lenne. A megnevezés a
          // fejlécben és a nagyító `aria-label`-jében ott van.
          <video
            src={current.src}
            controls
            className="max-h-full max-w-full rounded-lg bg-black shadow-2xl"
          />
        ) : (
          <img
            src={current.src}
            alt={current.alt}
            className={cn('max-h-full max-w-full rounded-lg object-contain shadow-2xl')}
          />
        )}

        {many && (
          <button
            type="button"
            onClick={() => step(1)}
            aria-label={t('lightbox.next')}
            className="ease-atlas absolute right-2 z-10 rounded-full bg-black/40 p-2 text-white/85 transition-colors duration-150 hover:bg-black/70 hover:text-white"
          >
            <ChevronRightIcon className="size-6" />
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}
